<script setup lang="ts">
import type { LinkNodeNodeViewProps } from "./types";
import { useQueryClient } from "@tanstack/vue-query";
import { NodeViewWrapper } from "@tiptap/vue-3";
import { SimplePopoverAnchor, SimplePopoverContent, SimplePopoverPortal, SimplePopoverRoot, SimplePopoverTrigger } from "ls/components/simple-popover";
import { queries } from "ls/features/bookmark/queries";
import LinkFrame from "ls/features/disko/links/LinkFrame.vue";
import { computed } from "vue";
import { isStandalone } from "@/common/environment";
import LinkBox from "./LinkBox.vue";
import LinkToolbar from "./LinkToolbar.vue";
import LinkToolbarEdit from "./LinkToolbarEdit.vue";
import MaximizeOverlayContainer from "./MaximizeOverlayContainer.vue";
import { useLinkNode } from "./useLinkNode";

const props = defineProps<LinkNodeNodeViewProps>();
if (!isStandalone) {
  // prefetch url metadata for accurately render the link toolbar
  // without metadata, link nodes can't decide if the Embed button is needed
  const queryClient = useQueryClient();
  queryClient.prefetchQuery(computed(() => queries.urlMetadata(props.node.attrs.url)));
}

const { isMaximized, showToolbar, selected, isPointer, handleLinkClick, handleUpdateOpen, close, isEditing } = useLinkNode(props);

function handleUnlink() {
  const pos = props.getPos();
  if (pos === undefined) return false;
  props.editor
    .chain()
    .setNodeSelection(pos)
    .unsetLinkNode()
    .focus()
    .run();
}
</script>

<template>
  <NodeViewWrapper
    as="a"
    :href="props.node.attrs.url"
    target="_blank"
    class="after:content-none!"
    @click="handleLinkClick"
  >
    <SimplePopoverRoot
      :open="showToolbar"
      :modal="false"
      @update:open="handleUpdateOpen"
    >
      <!-- use 1px anchor for popover content sizing to be minimal -->
      <SimplePopoverAnchor as="span" class="size-px" />
      <SimplePopoverTrigger as-child>
        <LinkBox
          variant="link"
          :active="showToolbar"
          :pointer="isPointer"
          :selected="selected"
        >
          <span>{{ props.node.attrs.text || props.node.attrs.url }}</span>
        </LinkBox>
      </SimplePopoverTrigger>

      <SimplePopoverPortal>
        <SimplePopoverContent
          side="top"
          align="start"
          data-axure-bubble-menu-content
          @open-auto-focus.prevent
        >
          <LinkToolbarEdit
            v-if="isEditing"
            v-bind="props"
            @close="close"
          />
          <LinkToolbar
            v-else
            v-bind="props"
            v-model:maximized="isMaximized"
            v-model:edit-mode="isEditing"
            @unlink="handleUnlink"
          />
        </SimplePopoverContent>
      </SimplePopoverPortal>
    </SimplePopoverRoot>
    <!--
        Use teleport to break outside of the NodeViewWrapper.
        This is important to prevent any side-effects of being inside an <a> tag, e.g. styling or click events.
    -->
    <Teleport
      v-if="isMaximized"
      to="body"
    >
      <MaximizeOverlayContainer
        v-model="isMaximized"
      >
        <LinkFrame :url="props.node.attrs.url" />
      </MaximizeOverlayContainer>
    </Teleport>
  </NodeViewWrapper>
</template>
